import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Overline,
  Separator,
  Button,
  InfoSheetPanel,
  Row,
  useOverlay,
  usePageWidth,
  H3,
  Paragraph,
  Subheading,
} from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useSlsHighlightsContext } from '@fs/zion-sls'
import RelationshipReviewCard from '../relationshipReviewCard/RelationshipReviewCard'
import { useFamilyReviewStateContext, useFamilyReviewDispatchContext, STEPS } from '../../providers/FamilyReviewContext'
import { getPrimaryName, getPrimaryOrFirstPerson, getAllEvents, groupEventsByPerson } from '../../utils/utils'
import { showEssentialInfoHighlights, showAllPersonsInfoHighlights } from '../../utils/highlightUtils'
import RecordTooltip from './RecordTooltip'
import IsEverythingAccountedForCard from './IsEverythingAccountedForCard'
import RecordTooltipButton from './RecordTooltipButton'
import HelpWithThisStep from '../faqContent/HelpWithThisStep'

export default function StepTwo({
  currentTask,
  handleNewPerson,
  handleGoToNextRecordAndResetStep,
  editPersonOverlay,
  stepTwoOverlay,
  stepThreeOverlay,
  hasImagePermission,
  setCardState,
  updateStepThreeData,
}) {
  const h = useSlsHighlightsContext()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const { t } = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const recordTooltipOverlay = useOverlay()

  const setStepThreeData = useCallback(() => {
    const events = getAllEvents(currentTask)
    const groupedEvents = groupEventsByPerson(events, currentTask)

    setCardState({
      elements: groupedEvents,
      stepStateCards: state.stepThree.eventCards,
      key: 'eventId',
      updateStepData: updateStepThreeData,
    })
  }, [currentTask, setCardState, state.stepThree.eventCards, updateStepThreeData])

  const goToStepThree = () => {
    setStepThreeData()
    stepThreeOverlay.open()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_THREE })
  }
  const nextButtonClicked = () => {
    goToStepThree()
  }

  const setHighlightsForNextCard = () => {
    const currentRelationshipCardIndex = state.stepTwo.relationshipCards.findIndex(
      (cardState) => cardState.personId === state.selectedPersonId
    )
    const nextPersonCard = state.stepTwo.relationshipCards[currentRelationshipCardIndex + 1]
    if (nextPersonCard) {
      showEssentialInfoHighlights({
        h,
        mainPartitionKey: currentTask.partitionKey,
        splitScreenPartitionKey: state.splitScreenPartitionKey,
        personIds: [nextPersonCard.personId],
        isSmallScreen,
      })
    }
  }

  const handleCorrectClick = (personId) => {
    dispatch({
      type: 'MARK_RELATIONSHIP_CARD_CORRECT',
      value: personId,
    })

    setTimeout(() => {
      const updatedCards = state.stepTwo.relationshipCards.map((card) =>
        card.personId === personId ? { ...card, hasBeenReviewed: true } : card
      )

      const allReviewed = updatedCards.every((card) => card.hasBeenReviewed)

      if (allReviewed) {
        showAllPersonsInfoHighlights({
          h,
          mainPartitionKey: currentTask.partitionKey,
          splitScreenPartitionKey: state.splitScreenPartitionKey,
          personIds: updatedCards.map((card) => card.personId),
          isSmallScreen,
        })
      } else {
        setHighlightsForNextCard()
      }
    }, 0)
  }

  const handleCloseStepThree = () => {
    stepTwoOverlay.close()
    if (state.currentStep === STEPS.STEP_TWO) dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_ONE })
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [getPrimaryOrFirstPerson(currentTask)?.id],
      isSmallScreen,
    })
  }

  const backButtonClicked = () => {
    handleCloseStepThree()
  }
  const userClosedPanel = () => {
    handleCloseStepThree()
  }

  return (
    <InfoSheetPanel
      {...{ ...stepTwoOverlay, close: userClosedPanel }}
      headingColor="purple"
      title={t('family-review.step3.heading', 'Family Relationships')}
      footer={
        <>
          {!state.stepTwo?.allReviewsComplete && (
            <>
              <Paragraph size="sm" secondary>
                {t(
                  'family-review.step2.must-review-everyone-message',
                  'You cannot move on until each person has been reviewed.'
                )}
              </Paragraph>
              <Separator size="sm" />
            </>
          )}
          <Row alignX="end">
            <Button onClick={backButtonClicked} data-testid="step-two-back-button" linkName="Step Two Go Back">
              {t('navigation.back-button', 'Back')}
            </Button>
            <Button
              disabled={!state.stepTwo?.allReviewsComplete}
              emphasis="high"
              onClick={nextButtonClicked}
              linkName="Step Two Next"
              data-testid="step-two-next-button"
            >
              {t('navigation.next-button', 'Next')}
            </Button>
          </Row>
        </>
      }
    >
      {stepTwoOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '2' })}</Overline>
            {t(
              'family-review.step2.main-instruction',
              'Review each person on this record and their relationship to {primaryPersonName}.',
              { primaryPersonName: getPrimaryName(currentTask) }
            )}{' '}
            <RecordTooltipButton overlay={recordTooltipOverlay} stepText="Two" />
            <Subheading>
              {t(
                'family-review.step2.subheading',
                'Click the Edit button to correct any highlight or spelling mistakes.'
              )}
            </Subheading>
          </H3>
          <RecordTooltip overlay={recordTooltipOverlay} />
          <HelpWithThisStep
            currentStep="2"
            hasImagePermission={hasImagePermission}
            handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
            personId={getPrimaryOrFirstPerson(currentTask)?.id}
          />
          <Separator size="sm" />
          {state.stepTwo.relationshipCards.map((cardState, index, array) => {
            return (
              <div key={cardState.personId}>
                <RelationshipReviewCard
                  personId={cardState.personId}
                  currentTask={currentTask}
                  editPersonOverlay={editPersonOverlay}
                  handleCorrectClick={handleCorrectClick}
                />
                {index < array.length - 1 && <Separator size="xs" />}
              </div>
            )
          })}
          <Separator size="sm" />
          {!state.stepTwo?.allReviewsComplete && (
            <Button Icon={ContentAdd} onClick={handleNewPerson} data-testid="add-person" linkName="Step Two Add Person">
              {t('family-review.step2.add-new-person-button', 'Add new person')}
            </Button>
          )}
          {state.stepTwo?.allReviewsComplete && (
            <IsEverythingAccountedForCard
              overlay={recordTooltipOverlay}
              handleNewEntity={handleNewPerson}
              handleClick={nextButtonClicked}
              isStepThree={false}
            />
          )}
        </>
      )}
    </InfoSheetPanel>
  )
}
